import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type { Condition } from '@/lib/expr'
import ConditionEditor from '../../components/ConditionEditor'
import type { ConditionField } from '../../components/ConditionEditor'
import type { FormRule, Severity } from '../../types'
import { isEmptyCondition, SEVERITIES } from './model'

const NONE = '__none__'

interface Props {
    open: boolean
    initial: FormRule
    isNew: boolean
    /** Ids used by the other rules. */
    otherIds: Set<string>
    fields: ConditionField[]
    onSave: (r: FormRule) => void
    onClose: () => void
}

/** Edits a copy of the rule; nothing is committed until Save. Mount it fresh (key) per rule. */
export default function RuleEditor({ open, initial, isNew, otherIds, fields, onSave, onClose }: Props) {
    const [r, setR] = useState<FormRule>(initial)
    const set = (patch: Partial<FormRule>) => setR((x) => ({ ...x, ...patch }))

    const idError = r.id.trim() === '' ? 'An id is required.' : otherIds.has(r.id) ? 'Another rule already uses this id.' : null
    const msgError = r.message.trim() === '' ? 'Say what the reviewer should see.' : null
    const whenError = isEmptyCondition(r.when) ? 'Add at least one condition.' : null
    const valid = !idError && !msgError && !whenError

    const clean = (): FormRule => {
        const out: FormRule = { ...r, id: r.id.trim() }
        if (!out.field) delete out.field
        if (!out.section?.trim()) delete out.section
        if (!out.moduleName?.trim()) delete out.moduleName
        return out
    }

    return (
        <Sheet open={open} onOpenChange={(o) => { if (!o) onClose() }}>
            <SheetContent className="w-full overflow-y-auto data-[side=right]:sm:max-w-xl">
                <SheetHeader>
                    <SheetTitle>{isNew ? 'New rule' : `Edit ${initial.id}`}</SheetTitle>
                    <SheetDescription>A rule raises a finding for the reviewer when its condition matches the answers.</SheetDescription>
                </SheetHeader>
                <div className="px-4">
                    <FieldGroup>
                        <Field data-invalid={!!msgError || undefined}>
                            <FieldLabel htmlFor="rule-message">Finding message</FieldLabel>
                            <Textarea id="rule-message" rows={3} value={r.message} aria-invalid={!!msgError || undefined}
                                placeholder="What the reviewer sees when this rule fires"
                                onChange={(e) => set({ message: e.target.value })} />
                            {msgError && <FieldError>{msgError}</FieldError>}
                        </Field>
                        <Field>
                            <FieldLabel id="rule-sev-label">Severity</FieldLabel>
                            <ToggleGroup type="single" variant="outline" size="sm" aria-labelledby="rule-sev-label" value={r.severity}
                                onValueChange={(v) => v && set({ severity: v as Severity })}>
                                {SEVERITIES.map((s) => <ToggleGroupItem key={s} value={s} className="capitalize">{s}</ToggleGroupItem>)}
                            </ToggleGroup>
                        </Field>
                        <Field>
                            <FieldLabel>Fires when</FieldLabel>
                            <ConditionEditor value={r.when} onChange={(c) => set({ when: (c ?? { all: [] }) as Condition })} fields={fields} emptyText="Add a condition" />
                            {whenError && <FieldError>{whenError}</FieldError>}
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="rule-field">Trigger answer</FieldLabel>
                            <Select value={r.field || NONE} onValueChange={(v) => set({ field: v === NONE ? undefined : v })}>
                                <SelectTrigger id="rule-field" className="w-full"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectGroup>
                                    <SelectItem value={NONE}>None</SelectItem>
                                    {fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                                </SelectGroup></SelectContent>
                            </Select>
                            <FieldDescription>The answer recorded on the finding as what triggered it.</FieldDescription>
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="rule-section">Section (optional)</FieldLabel>
                            <Input id="rule-section" value={r.section ?? ''} onChange={(e) => set({ section: e.target.value })} />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="rule-module">Module name (optional)</FieldLabel>
                            <Input id="rule-module" value={r.moduleName ?? ''} onChange={(e) => set({ moduleName: e.target.value })} />
                        </Field>
                        <Field data-invalid={!!idError || undefined}>
                            <FieldLabel htmlFor="rule-id">Rule id</FieldLabel>
                            <Input id="rule-id" className="font-mono text-xs" value={r.id} aria-invalid={!!idError || undefined} onChange={(e) => set({ id: e.target.value })} />
                            {idError ? <FieldError>{idError}</FieldError> : <FieldDescription>Stamped on every finding. Changing it affects reporting.</FieldDescription>}
                        </Field>
                    </FieldGroup>
                </div>
                <SheetFooter>
                    <Button disabled={!valid} onClick={() => onSave(clean())}>{isNew ? 'Add rule' : 'Save rule'}</Button>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}
